/**
 * PBRPostProcessing - PBR渲染后处理管线组件
 *
 * 【功能概述】
 * 实现专业级的HDR后处理管线，包括色调映射、色彩空间管理和渲染器物理光照配置。
 * 这是PBR渲染链路的最后一环，确保HDR渲染结果正确转换为显示设备可识别的LDR图像。
 *
 * 【技术架构】
 * - ACES Filmic色调映射：电影工业标准的HDR→LDR转换算法
 * - sRGB色彩空间管理：确保色彩在不同设备间的一致性表现
 * - 渲染器物理配置：优化Three.js渲染器的高级渲染参数
 * - 阴影映射优化：为透明材质渲染提供正确的阴影处理
 *
 * 【实现边界】
 * 当前版本专注于基础色调映射和渲染器配置。完整的Bloom、DOF等后处理效果
 * 需要react-three-fiber的EffectComposer支持，属于未来扩展范围。
 *
 * @component
 * @example
 * ```tsx
 * <PBRPostProcessing toneMappingExposure={1.2} enabled={true} />
 * ```
 */

import { useThree } from "@react-three/fiber";
import { useEffect } from "react";
import { ACESFilmicToneMapping, SRGBColorSpace } from "three";

interface PBRPostProcessingProps {
  /** 色调映射曝光值：控制HDR图像的整体亮度，范围通常0.5-3.0 */
  toneMappingExposure?: number;
  /** 后处理开关：控制是否应用ACES色调映射和渲染器优化 */
  enabled?: boolean;
}

export function PBRPostProcessing({
  toneMappingExposure = 1.0,
  enabled = true,
}: PBRPostProcessingProps) {
  const { gl } = useThree();

  // 【渲染器后处理配置】配置Three.js WebGLRenderer的高级渲染参数
  useEffect(() => {
    if (!enabled) return;

    // 🎯 配置备份：保存原始渲染器设置以便组件卸载时恢复
    const originalToneMapping = gl.toneMapping;
    const originalToneMappingExposure = gl.toneMappingExposure;
    const originalOutputColorSpace = gl.outputColorSpace;

    // ========================= ACES Filmic色调映射配置 =========================
    // 【标准】Academy of Motion Picture Arts and Sciences电影工业标准
    // 【优势】自然的高光压缩、保持色彩关系、避免颜色偏移
    gl.toneMapping = ACESFilmicToneMapping;           // 启用ACES算法
    gl.toneMappingExposure = toneMappingExposure;     // 设置曝光值：EV = log₂(exposure)

    // ========================= sRGB色彩空间管理 =========================
    // 【标准】IEC 61966-2-1 sRGB色彩空间规范
    // 【作用】确保渲染结果在不同显示设备间的色彩一致性
    gl.outputColorSpace = SRGBColorSpace;             // 输出sRGB色彩空间

    // ========================= 物理光照系统配置 =========================
    // 【说明】Three.js r150+版本中physicallyCorrectLights已默认启用
    // 物理正确光照包括：平方反比衰减、正确的光照单位、色温支持

    // ========================= 阴影映射优化 =========================
    // 【作用】为PBR材质提供正确的阴影接收和投射
    // 【注意】antialias需在Canvas创建时设置，此处无法修改
    gl.shadowMap.enabled = true;                      // 启用阴影映射

    return () => {
      // 🎯 清理机制：组件卸载时恢复渲染器原始状态，避免影响其他场景
      gl.toneMapping = originalToneMapping;
      gl.toneMappingExposure = originalToneMappingExposure;
      gl.outputColorSpace = originalOutputColorSpace;
    };
  }, [gl, toneMappingExposure, enabled]);

  // 🎯 无渲染组件：此组件仅负责渲染器配置，不产生任何可视化元素
  return null;
}

// ========================= 预设配置库 =========================
// 为不同应用场景提供优化的色调映射参数预设
export const PBRPostProcessingPresets = {
  /** 标准配置 - 平衡的视觉效果，适合大多数PBR场景 */
  standard: {
    toneMappingExposure: 1.0,  // EV=0，中性曝光
  },

  /** 高对比度配置 - 突出材质细节和反射效果 */
  highContrast: {
    toneMappingExposure: 1.4,  // EV=+0.5，增强对比度
  },

  /** 柔和曝光配置 - 更细腻的光影过渡，适合精密物体展示 */
  soft: {
    toneMappingExposure: 0.8,  // EV=-0.3，降低整体亮度
  },

  /** 电影级配置 - 戏剧性光照效果，适合艺术化展示 */
  cinematic: {
    toneMappingExposure: 1.6,  // EV=+0.7，电影级高曝光
  },
} as const;
